/**
 * GA4 Deduplication Engine
 * Prevents rapid duplicate events (e.g. double-click) and duplicate SPA page views.
 */

// Cache for recently fired events: Map<signature, timestamp>
const recentEvents = new Map<string, number>();

// State for SPA page views
let lastPagePath: string | null = null;
let lastPageTimestamp: number = 0;

const DEFAULT_EVENT_DEDUPE_WINDOW_MS = 400; // 400ms throttle for identical events
const PAGE_VIEW_DEDUPE_WINDOW_MS = 150; // 150ms throttle for identical route triggers

/**
 * Creates a unique signature for an event call.
 */
function createEventSignature(eventName: string, params: Record<string, any> = {}): string {
  try {
    // Sort keys for deterministic JSON representation
    const sortedKeys = Object.keys(params).sort();
    const sortedObj: Record<string, any> = {};
    for (const key of sortedKeys) {
      // Exclude timestamp/random params from signature if any
      if (key !== 'timestamp' && key !== '_ts') {
        sortedObj[key] = params[key];
      }
    }
    return `${eventName}:${JSON.stringify(sortedObj)}`;
  } catch {
    return `${eventName}:${Object.keys(params).join(',')}`;
  }
}

/**
 * Cleans up expired event signatures from the cache.
 */
function pruneRecentEvents(now: number, maxAge: number): void {
  for (const [sig, time] of recentEvents.entries()) {
    if (now - time > maxAge) {
      recentEvents.delete(sig);
    }
  }
}

/**
 * Checks if an event is a duplicate within the given window.
 * Returns `true` if duplicate (should be skipped), `false` if it should proceed.
 */
export function isDuplicateEvent(
  eventName: string,
  params: Record<string, any> = {},
  windowMs: number = DEFAULT_EVENT_DEDUPE_WINDOW_MS
): boolean {
  const now = Date.now();
  pruneRecentEvents(now, windowMs * 2);

  const signature = createEventSignature(eventName, params);
  const lastTime = recentEvents.get(signature);

  if (lastTime && now - lastTime < windowMs) {
    return true; // Duplicate detected
  }

  recentEvents.set(signature, now);
  return false;
}

/**
 * Checks if an SPA page view is a duplicate.
 * Returns `true` if duplicate (should be skipped), `false` if it should proceed.
 */
export function isDuplicatePageView(
  currentPath: string,
  windowMs: number = PAGE_VIEW_DEDUPE_WINDOW_MS
): boolean {
  const now = Date.now();

  // Normalize path (trim trailing slashes for uniformity, except root '/')
  const normalizedPath = currentPath.length > 1 ? currentPath.replace(/\/+$/, '') : currentPath;

  if (lastPagePath === normalizedPath && now - lastPageTimestamp < windowMs) {
    return true; // Duplicate page view detected
  }

  lastPagePath = normalizedPath;
  lastPageTimestamp = now;
  return false;
}

/**
 * Resets deduplication state (useful for tests and hard navigation).
 */
export function resetDeduplicationState(): void {
  recentEvents.clear();
  lastPagePath = null;
  lastPageTimestamp = 0;
}
